import React, { useEffect } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RevealWrapper } from '../hooks/useReveal';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { MessageSquare, Sparkles, Zap, Rocket } from 'lucide-react';

export const FAQPage = () => {
  // SEO: Update document title and meta description
  useEffect(() => {
    document.title = 'Nextcloud Hosting FAQ | Häufige Fragen | Wolkenhelden';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Häufig gestellte Fragen zu unserem Managed Nextcloud Hosting: Was ist inkludiert? Wie funktioniert die Migration? Ist es DSGVO-konform? Alle Antworten hier.');
    }
  }, []);

  const faqs = [
    {
      q: '🦸 Was ist „Managed Nextcloud" bei euch?',
      a: 'Du bekommst eine fertige Helden-Wolke (Nextcloud), und wir übernehmen Betrieb, Updates, Security, Monitoring und Stabilität. Du konzentrierst dich auf dein Team – wir kümmern uns um den Rest!'
    },
    {
      q: '☁️ Wo läuft meine Helden-Wolke?',
      a: 'In unserer hochverfügbaren Helden-Plattform mit S3-Objektspeicher als Hauptspeicher. Standorte: Frankfurt am Main (⚡ aktiv) und Hamburg (🔧 in Einrichtung). Alles in Deutschland – DSGVO-konform!'
    },
    {
      q: '💪 Kann ich später meinen Helden-Speicher erhöhen?',
      a: 'Klar! Du kannst im Helden-Portal jederzeit in 50-GB-Schritten deine Superkräfte upgraden. Mehr Power? Kein Problem!'
    },
    {
      q: '⚡ Kann ich Superkräfte (Add-ons) später ändern?',
      a: 'Ja. Team-Power (Team-Helden), Helden-Schutzschild (Antivirus) und den Turbo-Antrieb kannst du jederzeit anpassen.'
    },
    {
      q: '🔄 Was bedeutet „selbstheilend"?',
      a: 'Wenn einzelne Komponenten ausfallen, werden sie automatisch ersetzt/neugestartet – wie ein echter Superheld, der immer wieder aufsteht. Deine Wolke bleibt erreichbar!'
    },
    {
      q: '🚀 Wie schnell ist die Bereitstellung?',
      a: 'Nach Bestellung wird deine Helden-Wolke automatisch provisioniert. Du siehst den Status live im Portal. Die Einrichtung dauert meist nur wenige Minuten – schneller als du "Superheld" sagen kannst!'
    },
    {
      q: '📅 Gibt es eine Mindestlaufzeit?',
      a: 'Nein. Monatlich kündbar – ohne Drama, ohne Stress, ohne Knebelverträge. Echte Helden brauchen Freiheit!'
    },
    {
      q: '🤝 Ist Team-Power (Collabora Office) enthalten?',
      a: 'Optional. Du buchst genau so viele Helden, wie du brauchst (2,50 €/Monat pro Held). Gemeinsam Dokumente bearbeiten – heldenhaft!'
    },
    {
      q: '🏎️ Wofür ist der Turbo-Antrieb (High Performance)?',
      a: 'Für Nextcloud Talk & Video-Konferenzen! Der Turbo-Antrieb liefert die nötigen Ressourcen für flüssige Team-Kommunikation in Echtzeit. Ideal für Helden-Teams, die viel videotelefonieren. Volle Power voraus!'
    },
    {
      q: '🛡️ Was macht das Helden-Schutzschild (Antivirus)?',
      a: 'Es scannt Uploads zusätzlich auf bekannte Signaturen und erhöht so die Sicherheit deiner Wolke (5,00 €/Monat). Extra Schutz für echte Helden!'
    },
    {
      q: '🌐 Kann ich meine eigene Domain nutzen?',
      a: 'Ja, das ist sogar Pflicht! Wolkenhelden ist ein BYOD-Service (Bring Your Own Domain). Du benötigst eine eigene Domain oder Subdomain, die du per CNAME auf cloud.wolkenhelden.de zeigen lässt. Die Einrichtung ist einfach und wird im Checkout erklärt – wie eine Helden-Mission!'
    },
    {
      q: '💰 Wie läuft die Abrechnung?',
      a: 'Du bekommst Helden-Rechnungen automatisch per E-Mail und kannst sie jederzeit im Portal herunterladen. Transparent und ohne Überraschungen!'
    },
    {
      q: '☁️ Kann ich mehrere Helden-Wolken betreiben?',
      a: 'Ja! Im Helden-Portal kannst du mehrere Instanzen verwalten. Eine Wolke pro Team, pro Projekt oder pro Kunden – du entscheidest!'
    },
    {
      q: '📦 Bietet ihr Migration an?',
      a: 'Wenn du von einem anderen Anbieter umziehen willst, melde dich bei uns – unsere Helden helfen dir beim Umzug!'
    },
    {
      q: '🦸 Wie sieht Helden-Support aus?',
      a: 'Direkt im Helden-Portal: Ticket erstellen, Status verfolgen. Unser Helden-Team meldet sich schnellstmöglich. Wir lassen dich nicht im Stich!'
    },
    {
      q: '🔧 Wie richte ich den CNAME für meine Domain ein?',
      a: 'Logge dich bei deinem DNS-Provider ein und erstelle einen CNAME-Eintrag für deine (Sub-)Domain, der auf cloud.wolkenhelden.de zeigt. Beispiel: Wenn du cloud.beispiel.de nutzen willst, erstelle einen CNAME mit Name "cloud" und Ziel "cloud.wolkenhelden.de". Die Änderung ist meist nach 5-10 Minuten aktiv. Easy!'
    },
    {
      q: '💾 Gibt es Backups?',
      a: 'Ja! Regelmäßige automatische Backups sind inklusive – deine Daten sind heldenhaft abgesichert. Details zum Backup-Konzept findest du in der Dokumentation.'
    },
    {
      q: '🇩🇪 Ist das DSGVO-konform?',
      a: 'Absolut! Wir bieten AVV (Auftragsverarbeitungsvertrag) und Betrieb ausschließlich in Deutschland (Frankfurt/Hamburg). Details stehen in Datenschutz/AVV. Made in Germany!'
    },
    {
      q: '🧪 Kann ich testen?',
      a: 'Aktuell bieten wir keine Test-Phase an. Aber: Du kannst monatlich kündigen und startest mit dem heldenhaften Grundpaket ab nur 9 € – das Risiko ist minimal!'
    },
  ];

  return (
    <div className="py-16 md:py-24" data-testid="faq-page">
      <section className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <RevealWrapper>
          <div className="mb-12 text-center">
            <Badge variant="secondary" className="mb-4 bg-gradient-to-r from-primary/20 to-blue-500/20 border-primary/30">
              ❓ Helden-FAQ
            </Badge>
            <h1 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">
              Fragen? Wir haben <span className="text-primary">Antworten</span>! 🦸
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Alles, was du über deine Helden-Wolke wissen musst – kurz und klar.
            </p>
          </div>
        </RevealWrapper>

        <RevealWrapper delay={100}>
          <Card className="glass-card border-primary/10">
            <CardContent className="p-6">
              <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                {faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                    <AccordionTrigger className="text-left font-medium hover:text-primary">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </RevealWrapper>

        {/* Still have questions? */}
        <RevealWrapper delay={200}>
          <div className="mt-12 text-center">
            <Card className="glass-card border-primary/20 overflow-hidden relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-blue-500 to-primary" />
              <CardHeader className="pt-8">
                <div className="flex justify-center mb-4">
                  <div className="p-4 rounded-full bg-gradient-to-br from-primary to-blue-600 shadow-lg shadow-primary/30">
                    <MessageSquare className="h-8 w-8 text-white" />
                  </div>
                </div>
                <CardTitle className="font-display text-2xl">
                  Noch Fragen? Unsere <span className="text-primary">Helden</span> helfen! 🦸
                </CardTitle>
                <CardDescription className="text-base">
                  Schreib uns – wir antworten schneller als du "Wolke" sagen kannst.
                </CardDescription>
              </CardHeader>
              <CardContent className="pb-8">
                <div className="flex flex-wrap gap-4 justify-center">
                  <Button asChild className="bg-gradient-to-r from-primary to-blue-600">
                    <a href="mailto:support@wolkenhelden.de" data-testid="faq-contact-button" className="flex items-center gap-2">
                      <Zap className="h-4 w-4" />
                      Helden-Support kontaktieren
                    </a>
                  </Button>
                  <Button asChild variant="outline">
                    <Link to="/preise" className="flex items-center gap-2">
                      <Rocket className="h-4 w-4" />
                      Direkt loslegen
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </RevealWrapper>
      </section>
    </div>
  );
};
